import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Boxes, ChevronsLeft, ChevronsRight, ClipboardList, Cpu, LayoutDashboard, LifeBuoy, LogOut, Menu, Moon, RotateCcw,
  ShoppingBag, Sun, Users, Workflow, ShieldCheck, KeyRound, Settings2, FileText, FormInput, Building2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { label } from "@/lib/api";
import { ROUTE_PERMS, can } from "@/lib/roles";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/quotes", label: "Quotations", icon: FileText },
  { to: "/forms", label: "Lead Form", icon: FormInput },
  { to: "/b2b", label: "B2B Bulk Sales", icon: Building2 },
  { to: "/projects", label: "Projects", icon: ClipboardList },
  { to: "/inventory", label: "Inventory", icon: Boxes },
  { to: "/online-orders", label: "Online Orders", icon: ShoppingBag },
  { to: "/tickets", label: "Support Tickets", icon: LifeBuoy },
  { to: "/rma", label: "RMA", icon: RotateCcw },
  { to: "/settings/workflows", label: "Workflows", icon: Workflow, admin: true },
  { to: "/settings/users", label: "Role Manager", icon: ShieldCheck },
  { to: "/settings", label: "Settings", icon: Settings2 },
];

function MobileBar({ user, logout, theme, setTheme }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  const item = "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium";
  return (
    <div className="lg:hidden no-print sticky top-0 z-40 h-14 flex items-center gap-3 px-4 border-b bg-background/85 backdrop-blur-md" data-testid="mobile-topbar">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button className="-ml-1 p-1.5 rounded-md hover:bg-secondary" data-testid="mobile-menu-button" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-nav-drawer">
          <div className="h-14 flex items-center gap-2.5 px-4 border-b">
            <div className="h-7 w-7 rounded-md bg-foreground text-background flex items-center justify-center"><Cpu className="h-4 w-4" /></div>
            <span className="font-display font-bold">Gamerz Nation</span>
          </div>
          <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
            {NAV.filter((n) => can(user, ROUTE_PERMS[n.to])).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/settings"} data-testid={`mobile-nav-${n.to.replace(/\//g, "-").slice(1)}`}
                className={({ isActive }) => `${item} ${isActive ? "bg-secondary text-foreground" : "text-muted-foreground"}`}>
                <n.icon className="h-4 w-4" />{n.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-2 border-t space-y-0.5">
            <NavLink to="/change-password" className={`${item} text-muted-foreground`} data-testid="mobile-change-password"><KeyRound className="h-4 w-4" />Change password</NavLink>
            <button onClick={logout} className={`${item} w-full text-muted-foreground`} data-testid="mobile-logout-button"><LogOut className="h-4 w-4" />Log out</button>
            <div className="px-3 py-2 text-xs text-muted-foreground">{user?.name} · {label(user?.role)}</div>
          </div>
        </SheetContent>
      </Sheet>
      <div className="h-7 w-7 rounded-md bg-foreground text-background flex items-center justify-center"><Cpu className="h-4 w-4" /></div>
      <span className="font-display font-bold text-[15px]">Gamerz Nation</span>
      <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="ml-auto p-1.5 rounded-md hover:bg-secondary" data-testid="mobile-theme-toggle" aria-label="Toggle theme">
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(localStorage.getItem("gn_sidebar") === "1");
  const toggle = () => {
    localStorage.setItem("gn_sidebar", collapsed ? "0" : "1");
    setCollapsed(!collapsed);
  };
  return (
    <div className="flex min-h-screen">
      <aside className={`no-print sticky top-0 h-screen shrink-0 border-r bg-card/50 hidden lg:flex flex-col transition-[width] duration-200 ${collapsed ? "w-[60px]" : "w-60"}`}
        data-testid="sidebar">
        <div className="h-14 flex items-center gap-2.5 px-4 border-b">
          <div className="h-7 w-7 shrink-0 rounded-md bg-foreground text-background flex items-center justify-center">
            <Cpu className="h-4 w-4" />
          </div>
          {!collapsed && <div className="font-display font-bold text-[15px] tracking-tight truncate">Gamerz Nation</div>}
        </div>
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {NAV.filter((n) => can(user, ROUTE_PERMS[n.to])).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/settings"} data-testid={`nav-${n.to.replace(/\//g, "-").slice(1)}`} title={n.label}
              className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors ${
                isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}>
              <n.icon className="h-4 w-4 shrink-0" />
              {!collapsed && n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-2 border-t space-y-0.5">
          <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle"
            className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] text-muted-foreground hover:text-foreground hover:bg-secondary/60">
            {theme === "dark" ? <Sun className="h-4 w-4 shrink-0" /> : <Moon className="h-4 w-4 shrink-0" />}
            {!collapsed && (theme === "dark" ? "Light mode" : "Dark mode")}
          </button>
          <button onClick={toggle} data-testid="sidebar-collapse-toggle"
            className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] text-muted-foreground hover:text-foreground hover:bg-secondary/60">
            {collapsed ? <ChevronsRight className="h-4 w-4 shrink-0" /> : <ChevronsLeft className="h-4 w-4 shrink-0" />}
            {!collapsed && "Collapse"}
          </button>
          <div className="flex items-center gap-2.5 px-2.5 py-2">
            <div className="h-7 w-7 shrink-0 rounded-full bg-primary/15 text-primary text-[11px] font-semibold flex items-center justify-center">
              {user?.name?.split(" ").map((x) => x[0]).join("")}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium truncate" data-testid="current-user-name">{user?.name}</div>
                <div className="text-[11px] text-muted-foreground truncate" data-testid="current-user-role">{label(user?.role)}</div>
              </div>
            )}
            {!collapsed && (
              <NavLink to="/change-password" data-testid="change-password-link" className="text-muted-foreground hover:text-foreground" title="Change password">
                <KeyRound className="h-4 w-4" />
              </NavLink>
            )}
            {!collapsed && (
              <button onClick={logout} data-testid="logout-button" className="text-muted-foreground hover:text-foreground" title="Log out">
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <MobileBar user={user} logout={logout} theme={theme} setTheme={setTheme} />
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10 py-5 sm:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
